import React, { useCallback, useEffect, useRef, useState } from 'react'
import _ from 'lodash'

import { useApp } from '@/core/contexts/app'
import { classnames } from 'begonya/functions'

import { Column, Container, Row } from '@/components/base/gridview'
import Button from '@/components/base/button/Button'
import Image from '@/components/base/image/Image'
import Icon from '@/components/base/icon/Icon'

import query from '@/services/http/wap/query'
import { mapQuery } from '@/services/http/wap/query/mappers'

import Map from './Map'
import { LOCATION_MAPPING } from './constants'
import {
  ChargeLocationItem,
  ChargeLocationPrams,
  ChargeLocationProps,
} from './type'

const ChargeLocation: React.FunctionComponent<ChargeLocationProps> = ({
  title,
  apiKey,
  chargingTypes,
}) => {
  const app = useApp()

  const [filterData, setFilterData] = useState<Array<ChargeLocationItem>>()
  const [markerInfo, setMarkerInfo] = useState<ChargeLocationItem | null>(null)
  const [markerSticky, setMarkerSticky] = useState(false)
  const [activeMarker, setActiveMarker] = useState(-1)
  const [queryParams, setQueryParams] = useState<ChargeLocationPrams>({
    title: '',
    location: '',
    charge: '',
  })
  const mapRef = useRef<google.maps.Map | null>(null)
  const filterHeadRef = useRef<HTMLDivElement>(null)
  const [filterHead, setFilterHead] = useState(0)

  const defaultCenter = {
    lat: 39.1393768,
    lng: 34.2334807,
  }
  const [center, setCenter] = useState(defaultCenter)

  useEffect(() => {
    if (filterHeadRef.current) {
      setFilterHead(filterHeadRef.current.clientHeight)
    }
  }, [filterHead])

  const handleMarkerSelection = _.debounce(
    (marker: ChargeLocationItem, index: number) => {
      if (activeMarker === index && marker !== null) {
        setActiveMarker(-1)
        setMarkerInfo(null)
        setMarkerSticky(false)
        setCenter(defaultCenter)
        if (mapRef.current) {
          mapRef.current.setZoom(6)
        }
      } else {
        setMarkerSticky(true)
        setActiveMarker(index)
        setMarkerInfo(marker)
        if (mapRef.current) {
          mapRef.current.setZoom(15)
          mapRef.current.panTo({
            lat: Number(marker.lat),
            lng: Number(marker.lng),
          })
        }
      }
    },
    300
  )

  const getParamsAsQuery = (params: ChargeLocationPrams) => {
    const searchParams = new URLSearchParams()

    searchParams.set('pageSize', '20')

    if (Object.values(params).find((param) => param)) {
      if (params.title) {
        searchParams.set('title', params.title)
      }

      if (params.location) {
        searchParams.set('location', params.location)
      }

      if (params.charge) {
        searchParams.set('charge', params.charge)
      }
    }

    return searchParams.toString()
  }

  const getLocations = useCallback(async (queryString: string) => {
    try {
      const response = await query.run<ChargeLocationItem[]>(app.environment, {
        name: 'get-app-sarj-istasyonlari',
        language: app.language,
        query: queryString,
      })

      if (response) {
        const data = mapQuery(
          response.data,
          LOCATION_MAPPING
        ) as ChargeLocationItem[]

        setFilterData(data)
      }
    } catch (err) {
      console.log(err)
    }
  }, [])

  useEffect(() => {
    getLocations('')
  }, [getLocations])

  const handleTextfieldBlur = _.debounce(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const value = e.target.value
      setQueryParams((prevState) => ({
        ...prevState,
        title: value,
        location: value,
      }))
    },
    300
  )

  const handleClick = _.debounce((title: string) => {
    if (queryParams.charge === title) {
      setQueryParams((prevState) => ({
        ...prevState,
        charge: '',
      }))
    } else {
      setQueryParams((prevState) => ({
        ...prevState,
        charge: title,
      }))
    }
  }, 300)

  const handleInfo = () => {
    setMarkerSticky(!markerSticky)
  }

  useEffect(() => {
    const queryString = getParamsAsQuery(queryParams)
    getLocations(queryString)
  }, [getLocations, queryParams])

  const renderTranslatedText = (item: any) => {
    const isActiveAC = queryParams.charge === 'AC'
    const isActiveDC = queryParams.charge === 'DC'

    switch (item.title) {
      case 'AC':
        return (
          <Button
            onClick={() => handleClick(item.title)}
            className={classnames('button--ac', isActiveAC && 'button--active')}
          >
            <figure>
              <Image {...item.icon} alt={item.title} />
            </figure>
            {app.settings.translations['ac-charge']}
          </Button>
        )

      case 'DC':
        return (
          <Button
            onClick={() => handleClick(item.title)}
            className={classnames('button--dc', isActiveDC && 'button--active')}
          >
            <figure>
              <Image {...item.icon} alt={item.title} />
            </figure>
            {app.settings.translations['dc-charge']}
          </Button>
        )
      default:
        return item.title
    }
  }

  return (
    <div className="charge-locations">
      <Container size="fluid">
        <Row>
          <Column
            xs={{ size: 12 }}
            sm={{ size: 12 }}
            md={{ size: 12 }}
            lg={{ size: 4 }}
            xl={{ size: 4 }}
          >
            <div className="charge-locations-sidebar">
              <div ref={filterHeadRef} className="charge-locations-head">
                <div className="charge-locations-title">{title}</div>
                <div className="chrage-locations-filter">
                  <div className="search field">
                    <input
                      type="text"
                      placeholder="Konuma veya isme göre arama yapın"
                      onChange={handleTextfieldBlur}
                    />
                  </div>
                  <div className="energy-types field">
                    <div>
                      {chargingTypes?.map((item, index) => (
                        <React.Fragment key={index}>
                          {renderTranslatedText(item)}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div
                className="charge-locations-list"
                style={{ height: `calc(100% - ${filterHead}px)` }}
              >
                <div className="in">
                  {filterData?.map((item, index) => (
                    <div key={index}>
                      <Button
                        className={classnames(
                          activeMarker === index && 'button--active',
                          item.chargingType.title === 'AC' && 'button--ac',
                          item.chargingType.title === 'DC' && 'button--dc'
                        )}
                        onClick={() => handleMarkerSelection(item, index)}
                      >
                        <figure>
                          <Image
                            {...item.chargingType.icon}
                            alt={item.chargingType.title}
                          />
                        </figure>
                        <div className="desc">
                          {item.title}
                          <span>{item.location}</span>
                        </div>
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
              <div
                className={classnames(
                  'charge-locations-selected',
                  markerSticky && 'charge-locations-selected--active'
                )}
              >
                {markerInfo && (
                  <div>
                    <div
                      className={classnames(
                        'type',
                        markerInfo.chargingType.title === 'AC'
                          ? 'type--ac'
                          : 'type--dc'
                      )}
                    >
                      <figure>
                        <Image
                          {...markerInfo.chargingType.icon}
                          alt={markerInfo.chargingType.title}
                        />
                      </figure>
                      {markerInfo.chargingType.title === 'AC'
                        ? app.settings.translations['ac-charge']
                        : app.settings.translations['dc-charge']}
                    </div>
                    <div className="title">{markerInfo.title}</div>
                    <p
                      dangerouslySetInnerHTML={{ __html: markerInfo.address }}
                    />
                    <p
                      dangerouslySetInnerHTML={{
                        __html: markerInfo.directions,
                      }}
                    />
                    <Button
                      onClick={handleInfo}
                      className={classnames(
                        'handle-button',
                        !markerSticky && 'handle-button--disable'
                      )}
                    >
                      <Icon name="icon-arrow-left" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </Column>

          <Column
            xs={{ size: 12 }}
            sm={{ size: 12 }}
            md={{ size: 12 }}
            lg={{ size: 8 }}
            xl={{ size: 8 }}
          >
            <div className="charge-locations-map">
              <Map
                apiKey={apiKey}
                data={filterData}
                onMarkerSelect={handleMarkerSelection}
                center={center}
                mapRef={mapRef}
                setMarkerInfo={setMarkerInfo}
                activeMarker={activeMarker}
              />
            </div>
          </Column>
        </Row>
      </Container>
    </div>
  )
}

export default ChargeLocation
